import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  QrCode,
  Download,
  Printer,
  Share2,
  Link2,
  MessageCircle,
  Facebook,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { jsPDF } from 'jspdf';

const DEFAULT_URL = 'https://phciesada.org';
const PHARMACY_NAME = 'Pharmacie Saint Ange Des Amis';
const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

const MarketingTab = () => {
  const [url, setUrl] = useState(DEFAULT_URL);
  const [caption, setCaption] = useState(
    'Scannez pour visiter notre site et accéder à nos services 24h/24'
  );
  const qrRef = useRef(null);

  // Convert the currently rendered QR canvas to a data URL
  const getQrDataUrl = (size = 1024) => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return null;
    // Re-draw at requested size on a fresh canvas
    const off = document.createElement('canvas');
    off.width = size;
    off.height = size;
    const ctx = off.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(canvas, 0, 0, size, size);
    return off.toDataURL('image/png');
  };

  const downloadPng = () => {
    const dataUrl = getQrDataUrl(1024);
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `QR_${PHARMACY_NAME.replace(/\s+/g, '_')}.png`;
    link.click();
    toast.success('QR code PNG téléchargé');
  };

  const downloadPdf = () => {
    const dataUrl = getQrDataUrl(1024);
    if (!dataUrl) return;
    // Build a print-ready A4 portrait PDF
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();

    // Logo (top)
    try {
      doc.addImage(LOGO_URL, 'JPEG', pageW / 2 - 25, 15, 50, 50);
    } catch (e) {
      // ignore if logo fails
    }

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(93, 78, 55);
    doc.text(PHARMACY_NAME, pageW / 2, 80, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(196, 92, 38);
    doc.text('Le Compagnon de votre Santé', pageW / 2, 90, { align: 'center' });

    // QR code (centered)
    const qrSize = 120;
    doc.addImage(dataUrl, 'PNG', (pageW - qrSize) / 2, 100, qrSize, qrSize);

    // Caption
    doc.setFontSize(13);
    doc.setTextColor(60, 60, 60);
    const captionLines = doc.splitTextToSize(caption, 160);
    doc.text(captionLines, pageW / 2, 235, { align: 'center' });

    // URL
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(196, 92, 38);
    doc.text(url, pageW / 2, 255, { align: 'center' });

    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(
      'Ouvert 24h/24 les semaines de garde • Cocody II Plateaux',
      pageW / 2,
      285,
      { align: 'center' }
    );

    doc.save(`QR_${PHARMACY_NAME.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF prêt à imprimer téléchargé');
  };

  const printQr = () => {
    const dataUrl = getQrDataUrl(1024);
    if (!dataUrl) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html>
        <head><title>QR Code - ${PHARMACY_NAME}</title>
        <style>
          body { text-align: center; font-family: sans-serif; padding: 40px; color: #5D4E37; }
          img.logo { height: 80px; margin-bottom: 20px; }
          h1 { color: #5D4E37; margin: 8px 0; }
          h2 { color: #C45C26; font-weight: normal; margin: 4px 0 24px; font-size: 16px; }
          img.qr { width: 340px; height: 340px; }
          p { margin: 16px 40px; font-size: 14px; }
          .url { color: #C45C26; font-weight: bold; margin-top: 4px; }
        </style></head>
        <body>
          <img class="logo" src="${LOGO_URL}" />
          <h1>${PHARMACY_NAME}</h1>
          <h2>Le Compagnon de votre Santé</h2>
          <img class="qr" src="${dataUrl}" />
          <p>${caption}</p>
          <p class="url">${url}</p>
          <script>window.onload = () => { setTimeout(() => window.print(), 300); }</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const shareWhatsApp = () => {
    const msg = `${caption}\n${url}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(msg)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const shareSms = () => {
    const msg = `${caption} ${url}`;
    window.location.href = `sms:?body=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-6" data-testid="marketing-tab">
      <div>
        <h2 className="text-2xl font-bold text-[#5D4E37] flex items-center gap-2">
          <QrCode className="w-6 h-6 text-[#C45C26]" />
          Marketing & Visibilité
        </h2>
        <p className="text-[#8B7355] text-sm mt-1">
          Générez un QR code pour vos tickets, votre vitrine et vos supports imprimés.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* QR Preview */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6">
            <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0 mb-4">
              Aperçu du QR code
            </Badge>
            <div
              ref={qrRef}
              className="bg-white p-6 rounded-2xl border-2 border-[#E8DDD4] flex flex-col items-center gap-4"
            >
              <QRCodeCanvas
                value={url}
                size={256}
                level="H"
                includeMargin
                imageSettings={{
                  src: LOGO_URL,
                  height: 48,
                  width: 48,
                  excavate: true,
                }}
                data-testid="marketing-qr-canvas"
              />
              <p className="text-xs text-[#8B7355] text-center max-w-xs">
                {caption}
              </p>
              <p className="text-sm font-semibold text-[#C45C26] break-all text-center">
                {url}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                onClick={downloadPng}
                className="bg-[#C45C26] hover:bg-[#A84B1F] text-white flex-1 min-w-[140px]"
                data-testid="download-qr-png"
              >
                <Download className="w-4 h-4 mr-2" />
                PNG (1024px)
              </Button>
              <Button
                onClick={downloadPdf}
                className="bg-[#5D4E37] hover:bg-[#4A3E2A] text-white flex-1 min-w-[140px]"
                data-testid="download-qr-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF A4
              </Button>
              <Button
                onClick={printQr}
                variant="outline"
                className="border-[#C45C26] text-[#C45C26] hover:bg-[#C45C26]/10 flex-1 min-w-[140px]"
                data-testid="print-qr"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Config + Share */}
        <Card className="border-[#E8DDD4]">
          <CardContent className="p-6 space-y-5">
            <div>
              <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 mb-3">
                Personnalisation
              </Badge>
              <label className="block text-sm font-medium text-[#5D4E37] mb-1">
                URL du QR code
              </label>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://phciesada.org"
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                data-testid="qr-url-input"
              />
              <p className="text-xs text-[#8B7355] mt-1">
                Par défaut : votre page d'accueil. Vous pouvez pointer vers une page
                spécifique (ex: /garde pour la liste des pharmacies de garde).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#5D4E37] mb-1">
                Légende (imprimée sous le QR)
              </label>
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={140}
                className="border-[#E8DDD4] focus:border-[#C45C26]"
                data-testid="qr-caption-input"
              />
            </div>

            <div className="pt-3 border-t border-[#E8DDD4]">
              <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 mb-3">
                <Share2 className="w-3 h-3 mr-1" />
                Partager
              </Badge>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={shareWhatsApp}
                  variant="outline"
                  className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  onClick={shareFacebook}
                  variant="outline"
                  className="border-[#1877F2]/40 text-[#1877F2] hover:bg-[#1877F2]/10"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-4 h-4 mr-2" />
                  Facebook
                </Button>
                <Button
                  onClick={shareSms}
                  variant="outline"
                  className="border-[#5D4E37]/40 text-[#5D4E37] hover:bg-[#5D4E37]/10"
                  data-testid="share-sms"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  SMS
                </Button>
                <Button
                  onClick={copyLink}
                  variant="outline"
                  className="border-[#C45C26]/40 text-[#C45C26] hover:bg-[#C45C26]/10"
                  data-testid="copy-link"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier lien
                </Button>
              </div>
            </div>

            <div className="pt-3 border-t border-[#E8DDD4]">
              <p className="text-sm font-medium text-[#5D4E37] mb-2 flex items-center gap-1.5">
                <Link2 className="w-4 h-4" />
                Conseils d'utilisation
              </p>
              <ul className="text-xs text-[#8B7355] space-y-1 list-disc pl-5">
                <li>Imprimez le PDF A4 pour la vitrine et le comptoir</li>
                <li>Intégrez le QR code dans votre pied de ticket de caisse</li>
                <li>Ajoutez-le sur vos sachets, ordonnances et prospectus</li>
                <li>
                  Partagez le lien sur votre fiche{' '}
                  <a
                    href="https://business.google.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#C45C26] underline inline-flex items-center gap-0.5"
                  >
                    Google My Business <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MarketingTab;
